import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

const initialValue = { value: null };
const initialOrgUnit = { initial: true, loading: true, ...initialValue };

export const usePlanActionCache = (selectedOrgUnit) => {
  const [orgUnitStepCache, setOrgUnitStepCache] = useState({});

  const resetPlanActionCache = useCallback(() => setOrgUnitStepCache({}), []);
  const planActionStatus = useMemo(
    () =>
      selectedOrgUnit
        ? orgUnitStepCache[selectedOrgUnit.id] || initialOrgUnit
        : initialValue,
    [orgUnitStepCache, selectedOrgUnit],
  );

  const setOrgUnitCacheEntry = useCallback((orgUnitId, entry) => {
    setOrgUnitStepCache((prevState) => ({
      ...prevState,
      [orgUnitId]: entry,
    }));
  }, []);

  const mergeOrgUnitCacheEntry = useCallback((orgUnitId, entry) => {
    setOrgUnitStepCache((prevState) => ({
      ...prevState,
      [orgUnitId]: {
        ...prevState[orgUnitId],
        ...entry,
      },
    }));
  }, []);

  /**
   * @param hardReload boolean Whether or not to wipe current cache entries immediately
   */
  const reloadPlanActionStatus = useCallback(
    (hardReload, clearRemaining) => {
      if (selectedOrgUnit) {
        const { id } = selectedOrgUnit;
        // set loading indicator for each org unit separately
        if (hardReload) setOrgUnitCacheEntry(id, { loading: true });
        else mergeOrgUnitCacheEntry(id, { loading: true });
        if (clearRemaining) {
          setOrgUnitStepCache((prev) => ({
            [selectedOrgUnit.id]: prev[selectedOrgUnit.id],
          }));
        }
        HttpService.get(
          `${apiPaths.ORG_UNIT}${id}/plan_action_status/`,
          {},
          true,
        )
          .then(({ data }) => setOrgUnitCacheEntry(id, { value: data }))
          .catch((error) => setOrgUnitCacheEntry(id, { error: `${error}` }));
      }
    },
    [selectedOrgUnit, setOrgUnitCacheEntry, mergeOrgUnitCacheEntry],
  );

  useEffect(() => {
    if (planActionStatus && planActionStatus.initial)
      reloadPlanActionStatus(true);
  }, [planActionStatus, reloadPlanActionStatus]);

  const { i18n } = useTranslation();
  const cacheLang = useRef(i18n.language);

  useEffect(() => {
    if (cacheLang.current !== i18n.language) {
      cacheLang.current = i18n.language;
      // if language changes, reload current action plan status and drop all other cache entries
      reloadPlanActionStatus(false, true);
    }
  }, [i18n.language, reloadPlanActionStatus]);

  const increaseOrgUnitStep = (selectedIndex) => {
    if (!planActionStatus || !planActionStatus.value) return;
    const selectedStep = selectedIndex + 1;
    // if selected step is the previous step, decrease
    const nextStep =
      selectedStep === planActionStatus.value.step
        ? planActionStatus.value.step - 1
        : planActionStatus.value.step + 1;
    const { id } = selectedOrgUnit;
    mergeOrgUnitCacheEntry(id, { loading: true });
    HttpService.patch(`${apiPaths.ORG_UNIT}${id}/plan_action_status/`, {
      step: nextStep,
    })
      .then(({ data }) => {
        setOrgUnitCacheEntry(id, { value: data });
      })
      .catch((error) => setOrgUnitCacheEntry(id, { error: `${error}` }));
  };

  return {
    resetPlanActionCache,
    reloadPlanActionStatus,
    planActionStatus,
    increaseOrgUnitStep,
  };
};
